import { userPermissionsFromDb } from 'data/converters/permissionSets.converters';
import { TUserPermissionsUI, TNotificationKey } from 'data/types/permissions.types';
import { authSplitApi } from 'redux/helpers/slice.helpers';
import { EPropertyMangerUrl } from 'data/urls/propertyManager.url';
import { EPropertyUrl } from 'data/urls/property.url';
import {
  createPropertyConverter,
  getUnitsCoverter,
  propertiesGetConverter,
  propertiesConverter,
  propertiesTemplateTypeConverter,
  propertyCsvUploadConverter,
  propertyTypeConverter,
  propertyDetailsConverter,
  animalTypeManagementConverter,
  propertyDnaSettingConverter,
  propertyManagersTableConverter,
  invitePropertyManagerToDb,
  propertyManagerPropertiesTableConverter,
  unassignedPropertiesConverter,
  assignePropertiesConverter,
  uploadPetChargeStatusesConverter,
  propertyUploadedFilesConverter,
  residentsScreeningActionConverter,
  screeningConfigurationConverter,
  addUnitsConverter,
  propertyMetadataConverter,
  assigneTeamMembersConverter,
  teamMembersCsvUploadConverter,
} from 'data/converters/property.converters';
import {
  EPropertiesTemplateTypeUI,
  TCreatePropertyUI,
  TPropertiesCSVUploadResultDB,
  TPropertiesCSVUploadResultUI,
  TPropertiesDB,
  TPropertiesUI,
  TPropertiesTypeDB,
  TPropertiesTypeUI,
  TAnimalTypeManagementDB,
  TAnimalTypeManagementUI,
  TChangeAnimalTypeManagementUI,
  TUpdatePropertyDnaTestingUI,
  TPropertiesUploadUI,
  TPropertyDB,
  TPropertyTypeDB,
  TPropertyTypeUI,
  TPropertyUI,
  TUnitDB,
  TUnitUI,
  TUpdatePoilicyUI,
  TPropertyManagersTableUI,
  TPropertyManagersTableDB,
  TInvitePropertyManagerUI,
  TPropertyManagerPropertiesUI,
  TPropertyManagerPropertiesDB,
  TPropertyManagerDetailsUI,
  TPropertyManagerDetailsDB,
  TUnassignedPropertiesUI,
  TUnassignedPropertiesDB,
  TAssignPropertyUI,
  TPetChargeStatusesUploadUI,
  TPreopertyDeletePreviewUI,
  TPropertyUploadedFilesUI,
  TPropertyUploadedFilesDB,
  TResidentsScreeningActionUI,
  TScreeningConfigurationUI,
  TAddUnitsUI,
  ENotificationTypesDB,
  TPropertyMetadataDB,
  TPropertyMetadataUI,
  TCreateNotificationSetUI,
  TNotificationSettingsUI,
  TNotificationSetUpdateUI,
  TAnimalUpdateConfig,
  TTeamMember,
  TAssignTeamMembersUI,
  TPropertyPaymentStatistics,
  TPaymentSettings,
  TNotMatchedPropertyUI,
  TPropertiesConfigEditTypeUI,
  TTeamMembersUploadUI,
  TTeamMembersCSVUploadResultUI,
  TTeamMembersCSVUploadResultDB,
} from 'data/types/property.types';
import {
  TGetPropertyOnboardDetailsUI,
  TPageableDataParamUI,
  TPageableDataWithContent,
  TPageableDataWithContentUi,
  TPropertyOnboardDetailsUI,
} from 'data/types/pagableData.type';
import { queryParamConverter } from 'data/converters/general.converters';
import { isAllStatesPolicyState } from 'data/datas/policyState.data';
import { pageableDataConverter } from 'data/converters/pagableData.converters';
import {
  TIntegrationResultProps,
  TIntegrationResultPropsUI,
  TIntegrationType,
  TResidentsPetChargesUploadDataUI,
} from 'data/types/integration.types';
import { EIntegrationUrls } from 'data/urls/integration.url';
import {
  uploadTenantsPetChargesCSVExecuteConverter,
  uploadTenantsPetChargesCSVPreviewConverter,
} from 'data/converters/integration.converter';
import { TAddNewPropertyForm } from 'pages/private/Properties/PropertiesTable/AddNewPropertyModal/AddNewPropertyModal.types';
import { downloadFile } from 'helpers/fileServices';
import { formEntriesToTypedObject } from 'helpers/utils';
import { TPropertyWithAccessibilityInfoDB, TPropertyWithAccessibilityInfoUI } from 'data/types/propertyConfigSet.types';
import { propertyConfigSetEditerConverter } from 'data/converters/propertyConfigSet․converters';
import {
  TAffordableHandling,
  TFeeSuppression,
  TAffordableHandlingPmsPreview,
  TChangeFeeSuppression,
  TSuppressionImpactPreview,
  TUnitSuppressionStatus,
  EAffordableUnitHandling,
} from 'data/types/affordableSuppression.types';

const URL = `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTIES}`;
export const propertyApi = authSplitApi('propertyApi', [
  'propertiesSearch',
  'units',
  'propertyTypes',
  'properties',
  'property',
  'propertyManagers',
  'propertyManagerDetails',
  'propertyUploadedFiles',
  'assignedProperties',
  'unassignedProperties',
  'propertiesMetadata',
  'notificationSets',
  'teamMembers',
  // PP-9330: dedicated tags so flipping the toggle / changing fee
  // suppression doesn't invalidate the heavyweight `getProperty` cache.
  'affordableHandling',
  'feeSuppression',
  'unitSuppressionStatus',
]).injectEndpoints({
  endpoints: (build) => ({
    getPropertiesSearch: build.query<TPropertyUI[], { [key: string]: string | undefined } | null | void>({
      query(params) {
        // A residency without a policy reports a null policyState, and RTK
        // Query only strips undefined — a null would reach the API as the
        // literal string "null" and be rejected, emptying the picker. Drop
        // absent values here so no caller has to remember to coerce.
        const entries = Object.entries(params ?? {}) as [string, string | null | undefined][];
        const paramsCopy: Record<string, string> = {};
        entries.forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            paramsCopy[key] = value;
          }
        });
        if (paramsCopy.state && isAllStatesPolicyState(paramsCopy.state)) {
          delete paramsCopy.state;
        }
        return {
          url: `${URL}/${EPropertyUrl.SEARCH}`,
          method: 'GET',
          params: paramsCopy,
        };
      },
      transformResponse: (data: TPropertyDB[]) => data?.map((item) => propertiesGetConverter.fromDb(item)) || [],
      providesTags: ['propertiesSearch'], // for caching and update cache
    }),
    getUnits: build.query<TUnitUI[], { propertyId: string }>({
      query(params) {
        return {
          url: `${URL}/${EPropertyUrl.UNITS}`,
          method: 'GET',
          params,
        };
      },
      transformResponse: (data: TUnitDB[]) => data?.map((item) => getUnitsCoverter.fromDb(item)) || [],
      providesTags: ['units'],
    }),
    getPropertyTypes: build.query<TPropertyTypeUI[], string>({
      query() {
        return {
          url: `${URL}/${EPropertyUrl.TYPES}`,
          method: 'GET',
        };
      },
      providesTags: ['propertyTypes'],
      transformResponse: (data: TPropertyTypeDB[]) => data.map((item) => propertyTypeConverter.fromDb(item)),
    }),
    uploadPropertiesCSV: build.mutation<TPropertiesCSVUploadResultUI, TPropertiesUploadUI>({
      query(body) {
        return {
          url: `${URL}/${EPropertyUrl.UPLOAD}`,
          method: 'POST',
          body: propertyCsvUploadConverter.toDb(body),
        };
      },
      invalidatesTags: ['properties'],
      transformResponse: (data: TPropertiesCSVUploadResultDB) => propertyCsvUploadConverter.fromDb(data),
    }),
    previewCSVUploadResult: build.mutation<TPropertiesCSVUploadResultUI, TPropertiesUploadUI>({
      query(body) {
        return {
          url: `${URL}/${EPropertyUrl.PREVIEW_UPLOAD}`,
          method: 'POST',
          body: propertyCsvUploadConverter.toDb(body),
        };
      },
      transformResponse: (data: TPropertiesCSVUploadResultDB) => propertyCsvUploadConverter.fromDb(data),
    }),
    residentsPetChargesUploadPreview: build.mutation<TIntegrationResultPropsUI, TResidentsPetChargesUploadDataUI>({
      query(body) {
        return {
          url: `${URL}/${EIntegrationUrls.IMPORT}/${EIntegrationUrls.TENANTS_PET_CHARGES}/${EIntegrationUrls.PREVIEW}`,
          method: 'POST',
          body: uploadTenantsPetChargesCSVPreviewConverter.toDb(body),
        };
      },
      transformResponse: (data: TIntegrationResultProps) => uploadTenantsPetChargesCSVPreviewConverter.fromDb(data),
    }),
    residentsPetChargesUploadExecute: build.mutation<void, TResidentsPetChargesUploadDataUI>({
      query(body) {
        return {
          url: `${URL}/${EIntegrationUrls.IMPORT}/${EIntegrationUrls.TENANTS_PET_CHARGES}/${EIntegrationUrls.EXECUTE}`,
          method: 'POST',
          body: uploadTenantsPetChargesCSVExecuteConverter.toDb(body),
        };
      },
    }),
    getCSVTemplate: build.query<{ url: string }, { type: EPropertiesTemplateTypeUI }>({
      query(params) {
        return {
          url: `${URL}/${EPropertyUrl.TEMPLATE}`,
          method: 'GET',
          params: {
            ...params,
            type: propertiesTemplateTypeConverter.toDb(params.type),
          },
        };
      },
    }),
    createProperty: build.mutation<string, TCreatePropertyUI>({
      query(body) {
        return {
          url: `${URL}`,
          method: 'POST',
          body: createPropertyConverter.toDb(body),
        };
      },
      invalidatesTags: ['properties', 'assignedProperties'],
    }),
    updatePolicy: build.mutation<void, TUpdatePoilicyUI>({
      query(body) {
        return {
          url: `${URL}/${EPropertyUrl.POLICY}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['property'],
    }),
    getProperty: build.query<TPropertiesTypeUI, { id: string }>({
      query({ id }) {
        return {
          url: `${URL}/${id}`,
          method: 'GET',
        };
      },
      transformResponse: (data: TPropertiesTypeDB) => propertyDetailsConverter.fromDb(data),
      providesTags: ['property'],
    }),
    // PP-10057: the create-animal flows need only these three flags, so they read them on their
    // own rather than pulling the whole property.
    getPropertyAnimalTypeManagement: build.query<TAnimalTypeManagementUI, string>({
      query(propertyId) {
        return {
          url: `${URL}/${propertyId}/${EPropertyUrl.ANIMAL_TYPE_MANAGEMENT}`,
          method: 'GET',
        };
      },
      transformResponse: (data: TAnimalTypeManagementDB) => animalTypeManagementConverter.fromDb(data),
      providesTags: ['property'],
    }),
    // PP-10057: admin-only write; the current value rides along in GET /{id}, so this only
    // invalidates 'property'.
    changeAnimalTypeManagement: build.mutation<void, TChangeAnimalTypeManagementUI>({
      query({ propertyId, notManagedTypes }) {
        return {
          url: `${URL}/${propertyId}/${EPropertyUrl.ANIMAL_TYPE_MANAGEMENT}`,
          method: 'PATCH',
          body: animalTypeManagementConverter.toDb({ notManagedTypes }),
        };
      },
      invalidatesTags: ['property'],
    }),
    updatePropertyDnaTesting: build.mutation<void, TUpdatePropertyDnaTestingUI>({
      query({ propertyId, ...dnaTesting }) {
        return {
          url: `${URL}/${propertyId}/${EPropertyUrl.DNA}`,
          method: 'PATCH',
          body: { dnaSetting: propertyDnaSettingConverter.toDb(dnaTesting) },
        };
      },
      invalidatesTags: ['property'],
    }),
    getProperties: build.query<TPageableDataWithContentUi<TPropertiesUI[]>, TPageableDataParamUI>({
      query({ search, pagination }) {
        return {
          url: `${URL}`,
          method: 'GET',
          params: queryParamConverter(search, pagination, {}, true),
        };
      },
      transformResponse: (data: TPageableDataWithContent<TPropertiesDB[]>) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { content, ...other } = data;
        return {
          content: data.content.map((item) => propertiesConverter.fromDb(item)),
          paginationData: pageableDataConverter.fromDb(other),
        };
      },
      providesTags: ['properties'],
    }),
    deleteProperty: build.mutation<void, { id: string }>({
      query({ id }) {
        return {
          url: `${URL}/${id}`,
          method: 'DELETE',
        };
      },
    }),
    changePropertyName: build.mutation<void, { propertyId: string; name: string }>({
      query(body) {
        return {
          url: `${URL}/${EPropertyUrl.NAME}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['property'],
    }),
    changePropertyImage: build.mutation<void, { propertyId: string; photo: string }>({
      query(body) {
        return {
          url: `${URL}/${EPropertyUrl.PHOTO}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['property'],
    }),
    importPetChargeStatuses: build.mutation<void, TPetChargeStatusesUploadUI>({
      query(body) {
        return {
          url: `${URL}/${EPropertyUrl.PET_CHARGES}`,
          method: 'POST',
          body: uploadPetChargeStatusesConverter.toDb(body),
        };
      },
      invalidatesTags: ['property', 'propertyUploadedFiles'],
    }),
    getPetChargesCSVTemplate: build.query<{ url: string; name: string }, TIntegrationType>({
      query(type) {
        return {
          url: `${URL}/${EPropertyUrl.PET_CHARGES}/${EIntegrationUrls.TEMPLATE}`,
          method: 'GET',
          params: { integrationType: type },
        };
      },
    }),
    getPropertyOnboardDetails: build.query<TPropertyOnboardDetailsUI, TGetPropertyOnboardDetailsUI>({
      query(params) {
        return {
          url: `${URL}/${EPropertyUrl.ONBOARD}/${EPropertyUrl.DETAILS}`,
          method: 'GET',
          params,
        };
      },
    }),
    onboardProperty: build.mutation<void, { propertyId: string }>({
      query(body) {
        return {
          url: `${URL}/${EPropertyUrl.ONBOARD}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['property'],
    }),
    getPropertyManagers: build.query<TPageableDataWithContentUi<TPropertyManagersTableUI[]>, TPageableDataParamUI>({
      query({ search, pagination }) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTY_MANAGERS}`,
          method: 'GET',
          params: queryParamConverter(search, pagination),
        };
      },
      transformResponse: (data: TPageableDataWithContent<TPropertyManagersTableDB[]>) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { content, ...other } = data;
        return {
          content: data.content.map((item) => propertyManagersTableConverter.fromDb(item)),
          paginationData: pageableDataConverter.fromDb(other),
        };
      },
      providesTags: ['propertyManagers'],
    }),
    exportPropertyManagers: build.query<string, TPageableDataParamUI>({
      query({ search, pagination }) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTY_MANAGERS}/${EPropertyUrl.EXPORT}`,
          method: 'GET',
          params: queryParamConverter(search, pagination),
          responseHandler: async (response) => {
            if (response.ok) {
              const file = await response.blob();
              downloadFile(file, 'team-members.csv');
              return null;
            }
            return response.json();
          },
        };
      },
    }),
    invitePropertyManager: build.mutation<
      void,
      {
        data: TInvitePropertyManagerUI;
        permissions: TUserPermissionsUI;
        notifications: TNotificationKey[];
        animalUpdateConfig?: TAnimalUpdateConfig | null;
      }
    >({
      query(body) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTY_MANAGERS}/${EPropertyUrl.INVITATION}`,
          method: 'POST',
          body: invitePropertyManagerToDb(body),
        };
      },
      invalidatesTags: ['propertyManagers'],
    }),
    reInviteProperyManager: build.mutation<void, { propertyManagerId: string }>({
      query(body) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTY_MANAGERS}/${EPropertyUrl.INVITATION}/${EPropertyUrl.RESEND}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['propertyManagers'],
    }),
    deleteProperyManager: build.mutation<void, { propertyManagerId: string }>({
      query({ propertyManagerId }) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTY_MANAGERS}/${propertyManagerId}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: ['propertyManagers'],
    }),
    getPropertyManagersAssigned: build.query<
      TPageableDataWithContentUi<TPropertyManagerPropertiesUI[]>,
      TPageableDataParamUI & { propertyManagerId: string }
    >({
      query({ search, pagination, propertyManagerId }) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTY_MANAGERS}/${EPropertyUrl.PROPERTIES}/${EPropertyUrl.ASSIGNED}`,
          method: 'GET',
          params: queryParamConverter(search, pagination, {
            propertyManagerId,
          }),
        };
      },
      transformResponse: (data: TPageableDataWithContent<TPropertyManagerPropertiesDB[]>) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { content, ...other } = data;
        return {
          content: data.content.map((item) => propertyManagerPropertiesTableConverter.fromDb(item)),
          paginationData: pageableDataConverter.fromDb(other),
        };
      },
      providesTags: ['propertyManagerDetails'],
    }),
    getPropertyManagerDetails: build.query<TPropertyManagerDetailsUI, { id: string }>({
      query({ id }) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTY_MANAGERS}/${id}`,
          method: 'GET',
        };
      },
      transformResponse: (data: TPropertyManagerDetailsDB): TPropertyManagerDetailsUI => ({
        ...(data as unknown as TPropertyManagerDetailsUI),
        permissions: data.permissions ? userPermissionsFromDb(data.permissions) : undefined,
      }),
      providesTags: ['propertyManagerDetails'],
    }),
    unassignProperty: build.mutation<void, { properties: string[]; propertyManagerId: string }>({
      query(body) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTY_MANAGERS}/${EPropertyUrl.PROPERTIES}/${EPropertyUrl.UNASSIGN}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['assignedProperties', 'propertyManagerDetails', 'unassignedProperties'],
    }),
    getUnassignedProperties: build.query<TUnassignedPropertiesUI[], { propertyManagerId: string }>({
      query(params) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTY_MANAGERS}/${EPropertyUrl.PROPERTIES}/${EPropertyUrl.UNASSIGNED}`,
          method: 'GET',
          params,
        };
      },
      providesTags: ['unassignedProperties'],
      transformResponse: (data: TUnassignedPropertiesDB[]) =>
        (data || []).map((item) => unassignedPropertiesConverter.fromDb(item)),
    }),
    assignProperty: build.mutation<void, TAssignPropertyUI>({
      query(body) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTY_MANAGERS}/${EPropertyUrl.PROPERTIES}/${EPropertyUrl.ASSIGN}`,
          method: 'PATCH',
          body: assignePropertiesConverter.toDb(body),
        };
      },
      invalidatesTags: ['propertyManagerDetails', 'assignedProperties'],
    }),
    getDeletedPropertyPreview: build.query<TPreopertyDeletePreviewUI, { id: string }>({
      query({ id }) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTIES}/${EPropertyUrl.DELETE}/${EPropertyUrl.PREVIEW}/${id}`,
          method: 'GET',
        };
      },
    }),
    getPropertyUploadedFiles: build.query<TPageableDataWithContentUi<TPropertyUploadedFilesUI[]>, TPageableDataParamUI>(
      {
        query({ search, pagination, queryParams }) {
          return {
            url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTIES}/${EPropertyUrl.UPLOADED_FILES}`,
            method: 'GET',
            params: queryParamConverter(search, pagination, queryParams),
          };
        },
        providesTags: ['propertyUploadedFiles'],
        transformResponse: (data: TPageableDataWithContent<TPropertyUploadedFilesDB[]>) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { content, ...other } = data;
          return {
            content: data.content.map((item) => propertyUploadedFilesConverter.fromDb(item)),
            paginationData: pageableDataConverter.fromDb(other),
          };
        },
      }
    ),
    residentsScreeningAction: build.mutation<void, TResidentsScreeningActionUI>({
      query(body) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTIES}/${EPropertyUrl.TENANTS_SCREENING}`,
          method: 'PATCH',
          body: residentsScreeningActionConverter.toDb(body),
        };
      },
      invalidatesTags: ['property'],
    }),
    changeScreeningConfiguration: build.mutation<void, TScreeningConfigurationUI>({
      query(body) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTIES}/${EPropertyUrl.SCREENING_CONFIGURATION}`,
          method: 'PATCH',
          body: screeningConfigurationConverter.toDb(body),
        };
      },
      invalidatesTags: ['property'],
    }),
    updateAccessToFuture: build.mutation<void, { hasAccessToFuture: boolean; propertyManagerId: string }>({
      query(body) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTY_MANAGERS}/${EPropertyUrl.FUTURE_PROPERTIES}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['propertyManagerDetails'],
    }),
    // @deprecated — replaced by permissions capabilities (ACCESS_TO_BILLING_METHOD)
    // updateAccessToBillingMethod: build.mutation<void, { accessToBillingMethod: boolean; propertyManagerId: string }>({
    //   query(body) {
    //     return {
    //       url: `${EPropertyMangerUrl.ADMIN}/${EPropertyUrl.PROPERTY_MANAGEMENT}/${EPropertyUrl.BILLING_METHOD}`,
    //       method: 'PATCH',
    //       body,
    //     };
    //   },
    //   invalidatesTags: ['propertyManagerDetails'],
    // }),
    // @deprecated — replaced by permissions type (READ_ONLY)
    // updateReadOnlyAccess: build.mutation<void, { readOnlyAccess: boolean; propertyManagerId: string }>({
    //   query(body) {
    //     return {
    //       url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTY_MANAGERS}/read-only-access`,
    //       method: 'PATCH',
    //       body,
    //     };
    //   },
    //   invalidatesTags: ['propertyManagerDetails'],
    // }),
    addUnits: build.mutation<string[], TAddUnitsUI>({
      query(body) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTIES}/${body.propertyId}/${EPropertyUrl.UNIT}`,
          method: 'POST',
          body: addUnitsConverter.toDb(body),
        };
      },
      invalidatesTags: ['propertyManagers'],
    }),
    updateNotificationsToFuture: build.mutation<
      void,
      {
        notificationsEnabledForFutureProperties: boolean;
        propertyManagerId: string;
      }
    >({
      query(body) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTY_MANAGERS}/${EPropertyUrl.FUTURE_PROPERTIES_NOTIFICATIONS}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['propertyManagerDetails'],
    }),
    subscribeToNotification: build.mutation<
      void,
      {
        notificationTypes: ENotificationTypesDB[];
        propertyManagerId: string;
      }
    >({
      query(body) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTY_MANAGERS}/${EPropertyUrl.NOTIFICATION}/${EPropertyUrl.SUBSCRIBE}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['propertyManagerDetails'],
    }),
    unsubscribeFromNotification: build.mutation<
      void,
      {
        notificationTypes: ENotificationTypesDB[];
        propertyManagerId: string;
      }
    >({
      query(body) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTY_MANAGERS}/${EPropertyUrl.NOTIFICATION}/${EPropertyUrl.UNSUBSCRIBE}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['propertyManagerDetails'],
    }),
    enableNotificationsForProperties: build.mutation<
      void,
      {
        properties: string[];
        propertyManagerId: string;
      }
    >({
      query(body) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTY_MANAGERS}/${EPropertyUrl.PROPERTIES}/${EPropertyUrl.NOTIFICATIONS}/${EPropertyUrl.ENABLE}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['propertyManagerDetails', 'assignedProperties'],
    }),
    disableNotificationsForProperties: build.mutation<
      void,
      {
        properties: string[];
        propertyManagerId: string;
      }
    >({
      query(body) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTY_MANAGERS}/${EPropertyUrl.PROPERTIES}/${EPropertyUrl.NOTIFICATIONS}/${EPropertyUrl.DISABLE}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['propertyManagerDetails', 'assignedProperties'],
    }),
    markAsPrimaryContact: build.mutation<
      void,
      {
        propertyManagerId: string;
      }
    >({
      query(body) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTY_MANAGERS}/${EPropertyUrl.CONACT}/${EPropertyUrl.PRIMARY}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['propertyManagerDetails'],
    }),
    markAsNonPrimaryContact: build.mutation<
      void,
      {
        propertyManagerId: string;
      }
    >({
      query(body) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTY_MANAGERS}/${EPropertyUrl.CONACT}/${EPropertyUrl.NON_PRIMARY}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['propertyManagerDetails'],
    }),

    getPropertiesMetadata: build.query<TPropertyMetadataUI[], void>({
      query() {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTIES}/${EPropertyUrl.METADATA}`,
          method: 'GET',
        };
      },
      transformResponse: (data: TPropertyMetadataDB[]) =>
        (data || []).map((item) => propertyMetadataConverter.fromDb(item)),
    }),
    createNotificationSet: build.mutation<string[], TCreateNotificationSetUI>({
      query(body) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PM_NOTIFICATION_SETTINGS}`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['notificationSets'],
    }),
    getNotificationSets: build.query<TNotificationSettingsUI[], { propertyManagerId: string }>({
      query(params) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PM_NOTIFICATION_SETTINGS}`,
          method: 'GET',
          params,
        };
      },
      providesTags: ['notificationSets'],
    }),
    updateNotificationSet: build.mutation<void, TNotificationSetUpdateUI>({
      query(body) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PM_NOTIFICATION_SETTINGS}`,
          method: 'PUT',
          body,
        };
      },
      invalidatesTags: ['notificationSets'],
    }),
    deleteNotificationSet: build.mutation<void, { notificationSettingsId: string; propertyManagerId: string }>({
      query(body) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PM_NOTIFICATION_SETTINGS}`,
          method: 'DELETE',
          body,
        };
      },
      invalidatesTags: ['notificationSets'],
    }),
    getPropertyDeletePreview: build.query<
      TNotMatchedPropertyUI[],
      { propertyIds: string[]; allSelected: boolean; search: string }
    >({
      query({ propertyIds, allSelected, search }) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTIES}/${EPropertyUrl.DELETE}/${EPropertyUrl.PREVIEW}`,
          method: 'GET',
          params: queryParamConverter(search, undefined, {
            propertyIds,
            allSelected,
          }),
        };
      },
    }),
    deletePropertiesBulk: build.mutation<void, { propertyIds: string[]; allSelected: boolean; search: string }>({
      query({ propertyIds, allSelected, search }) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTIES}/${EPropertyUrl.BULK}`,
          method: 'DELETE',
          params: queryParamConverter(search, undefined, {
            propertyIds,
            allSelected,
          }),
        };
      },
      invalidatesTags: ['properties'],
    }),
    exportProperties: build.query<string, { propertyIds: string[]; exportAll: boolean; search: string }>({
      query({ propertyIds, exportAll, search }) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTIES}/${EPropertyUrl.EXPORT}`,
          method: 'GET',
          params: queryParamConverter(search, undefined, {
            propertyIds,
            exportAll,
          }),
          responseHandler: async (response) => {
            if (response.ok) {
              const file = await response.blob();
              downloadFile(file, 'properties.csv');
              return null;
            }
            return response.json();
          },
        };
      },
    }),
    updatePolicyBulk: build.mutation<
      void,
      {
        propertyIds: string[];
        allSelected: boolean;
        policyId: string;
        search: string;
      }
    >({
      query({ propertyIds, allSelected, search, policyId }) {
        const rawEntries = queryParamConverter(search, undefined, {
          propertyIds,
          allSelected,
          policyId,
        });
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTIES}/${EPropertyUrl.BULK}/${EPropertyUrl.POLICY}`,
          method: 'PATCH',
          body: { ...formEntriesToTypedObject(rawEntries), propertyIds },
        };
      },
    }),
    getPropertiesAccessibilityInfo: build.query<TPropertyWithAccessibilityInfoUI[], void>({
      query() {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTIES}/accessibility-info`,
          method: 'GET',
        };
      },
      transformResponse: (data: TPropertyWithAccessibilityInfoDB[]) => {
        return data;
      },
    }),
    getTeamMembers: build.query<TTeamMember[], { propertyId: string }>({
      query(params) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTIES}/${EPropertyUrl.TEAM_MEMBERS}`,
          method: 'GET',
          params,
        };
      },
      providesTags: ['teamMembers'],
    }),
    assignTeamMembersToProperty: build.mutation<void, TAssignTeamMembersUI>({
      query(body) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTIES}/${EPropertyUrl.TEAM_MEMBERS}/${EPropertyUrl.ASSIGN}`,
          method: 'POST',
          body: assigneTeamMembersConverter.toDb(body),
        };
      },
      invalidatesTags: ['teamMembers'],
    }),
    unassignTeamMembersFromProperty: build.mutation<void, { propertyId: string; teamMemberIds: string[] }>({
      query(body) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTIES}/${EPropertyUrl.TEAM_MEMBERS}/${EPropertyUrl.UNASSIGN}`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['teamMembers'],
    }),
    getPropertyPaymentStatistics: build.query<TPropertyPaymentStatistics, { propertyId: string }>({
      query({ propertyId }) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTIES}/${propertyId}/${EPropertyUrl.PAYMENT_STATISTICS}`,
          method: 'GET',
        };
      },
    }),
    updatePropertyPaymentSettings: build.mutation<
      void,
      { propertyId: string; propertyPaymentSettings: TPaymentSettings; applyRemovalRetroactively?: boolean }
    >({
      query(body) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTIES}/${EPropertyUrl.PAYMENT_SETTINGS}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['property'],
    }),
    updatePropertyInfo: build.mutation<void, TAddNewPropertyForm & { propertyId: string }>({
      query(body) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTIES}/${body.propertyId}`,
          method: 'PUT',
          body,
        };
      },
      invalidatesTags: ['property'],
    }),
    editConfigSet: build.mutation<void, TPropertiesConfigEditTypeUI>({
      query(body) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/animal-verification-configuration`,
          method: 'PUT',
          body: propertyConfigSetEditerConverter.toDb(body),
        };
      },
      invalidatesTags: ['property'],
    }),
    reassignConfigSet: build.mutation<void, { propertyId: string; animalVerificationConfigurationId: string }>({
      query(body) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTIES}/animal-verification-configuration`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['property'],
    }),
    // Team Members CSV Upload endpoints
    getTeamMembersCSVTemplate: build.query<{ templateUrl: string }, void>({
      query() {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTY_MANAGERS}/import/template`,
          method: 'GET',
        };
      },
    }),
    previewTeamMembersCSVUpload: build.mutation<TTeamMembersCSVUploadResultUI, TTeamMembersUploadUI>({
      query(body) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTY_MANAGERS}/import/preview`,
          method: 'POST',
          body: teamMembersCsvUploadConverter.toDb(body),
        };
      },
      transformResponse: (data: TTeamMembersCSVUploadResultDB) => teamMembersCsvUploadConverter.fromDb(data),
    }),
    uploadTeamMembersCSV: build.mutation<TTeamMembersCSVUploadResultUI, TTeamMembersUploadUI>({
      query(body) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTY_MANAGERS}/import/execute`,
          method: 'POST',
          body: teamMembersCsvUploadConverter.toDb(body),
        };
      },
      transformResponse: (data: TTeamMembersCSVUploadResultDB) => teamMembersCsvUploadConverter.fromDb(data),
      invalidatesTags: ['propertyManagers'],
    }),

    // PP-9331: affordable-units handling
    getAffordableHandling: build.query<TAffordableHandling, { propertyId: string }>({
      query: ({ propertyId }) => ({
        url: `${URL}/${propertyId}/${EPropertyUrl.AFFORDABLE_HANDLING}`,
        method: 'GET',
      }),
      providesTags: ['affordableHandling'],
    }),
    changeAffordableHandling: build.mutation<void, { propertyId: string; handling: EAffordableUnitHandling }>({
      query: ({ propertyId, handling }) => ({
        url: `${URL}/${propertyId}/${EPropertyUrl.AFFORDABLE_HANDLING}`,
        method: 'PATCH',
        body: { handling },
      }),
      // Flipping affordable handling can change the set of units classified
      // as affordable (sync runs in afterCommit), which in turn can change
      // per-unit suppression status when fee mode is ALL_AFFORDABLE.
      // It can also prune feeSuppression.customUnitIds server-side: an
      // ALL_AFFORDABLE-mode cascade re-derives per-unit flags from the new
      // affordable set, so the cached fee-suppression view goes stale.
      invalidatesTags: ['affordableHandling', 'feeSuppression', 'unitSuppressionStatus'],
    }),
    getAffordableHandlingPmsPreview: build.query<TAffordableHandlingPmsPreview, { propertyId: string }>({
      query: ({ propertyId }) => ({
        url: `${URL}/${propertyId}/${EPropertyUrl.AFFORDABLE_HANDLING}/${EPropertyUrl.PMS_PREVIEW}`,
        method: 'GET',
      }),
      // Don't cache — admin expects fresh PMS data each time the dialog opens.
      keepUnusedDataFor: 0,
    }),

    // PP-9332: fee-suppression mode + per-unit status
    getFeeSuppression: build.query<TFeeSuppression, { propertyId: string }>({
      query: ({ propertyId }) => ({
        url: `${URL}/${propertyId}/${EPropertyUrl.FEE_SUPPRESSION}`,
        method: 'GET',
      }),
      providesTags: ['feeSuppression'],
    }),
    changeFeeSuppression: build.mutation<void, { propertyId: string } & TChangeFeeSuppression>({
      query: ({ propertyId, mode, customUnitIds }) => ({
        url: `${URL}/${propertyId}/${EPropertyUrl.FEE_SUPPRESSION}`,
        method: 'PATCH',
        body: { mode, customUnitIds },
      }),
      // Mode change cascades to unit.suppress_pet_profile_fee on every unit,
      // so per-unit status needs a refetch too. Doesn't touch the rest of
      // the property aggregate.
      invalidatesTags: ['feeSuppression', 'unitSuppressionStatus'],
    }),
    previewFeeSuppression: build.mutation<TSuppressionImpactPreview, { propertyId: string } & TChangeFeeSuppression>({
      query: ({ propertyId, mode, customUnitIds }) => ({
        url: `${URL}/${propertyId}/${EPropertyUrl.FEE_SUPPRESSION}/${EPropertyUrl.PREVIEW}`,
        method: 'POST',
        body: { mode, customUnitIds },
      }),
    }),
    getUnitSuppressionStatus: build.query<TUnitSuppressionStatus[], { propertyId: string }>({
      query: ({ propertyId }) => ({
        url: `${URL}/${propertyId}/${EPropertyUrl.UNITS}/${EPropertyUrl.SUPPRESSION_STATUS}`,
        method: 'GET',
      }),
      providesTags: ['unitSuppressionStatus'],
    }),

    residentsSkipAutoScreeningEnable: build.mutation<void, { propertyId: string }>({
      query(body) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTIES}/skip-auto-screening-for-renewal-residents/enable`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['property'],
    }),
    residentsSkipAutoScreeningDisable: build.mutation<void, { propertyId: string }>({
      query(body) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPropertyUrl.PROPERTIES}/skip-auto-screening-for-renewal-residents/disable`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['property'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetPropertyUploadedFilesQuery,
  useGetPropertiesSearchQuery,
  useGetUnitsQuery,
  useGetPropertyTypesQuery,
  useGetCSVTemplateQuery,
  useUploadPropertiesCSVMutation,
  usePreviewCSVUploadResultMutation,
  useCreatePropertyMutation,
  useUpdatePolicyMutation,
  useGetPropertyQuery,
  useUpdatePropertyDnaTestingMutation,
  useChangeAnimalTypeManagementMutation,
  useGetPropertyAnimalTypeManagementQuery,
  useGetPropertiesQuery,
  useDeletePropertyMutation,
  useChangePropertyNameMutation,
  useChangePropertyImageMutation,
  useGetPropertyManagersQuery,
  useLazyGetPropertyManagersQuery,
  useInvitePropertyManagerMutation,
  useReInviteProperyManagerMutation,
  useDeleteProperyManagerMutation,
  useGetPropertyManagersAssignedQuery,
  useGetPropertyManagerDetailsQuery,
  useUnassignPropertyMutation,
  useGetUnassignedPropertiesQuery,
  useAssignPropertyMutation,
  useGetPropertyOnboardDetailsQuery,
  useOnboardPropertyMutation,
  useImportPetChargeStatusesMutation,
  useGetPetChargesCSVTemplateQuery,
  useGetDeletedPropertyPreviewQuery,
  useResidentsScreeningActionMutation,
  useChangeScreeningConfigurationMutation,
  useResidentsPetChargesUploadExecuteMutation,
  useResidentsPetChargesUploadPreviewMutation,
  useUpdateAccessToFutureMutation,
  useAddUnitsMutation,
  useUpdateNotificationsToFutureMutation,
  useSubscribeToNotificationMutation,
  useUnsubscribeFromNotificationMutation,
  useEnableNotificationsForPropertiesMutation,
  useDisableNotificationsForPropertiesMutation,
  useMarkAsNonPrimaryContactMutation,
  useMarkAsPrimaryContactMutation,
  useGetPropertiesMetadataQuery,
  useCreateNotificationSetMutation,
  useGetNotificationSetsQuery,
  useUpdateNotificationSetMutation,
  useDeleteNotificationSetMutation,
  useGetTeamMembersQuery,
  useAssignTeamMembersToPropertyMutation,
  useUnassignTeamMembersFromPropertyMutation,
  useGetPropertyPaymentStatisticsQuery,
  useUpdatePropertyPaymentSettingsMutation,
  useUpdatePropertyInfoMutation,
  useLazyGetPropertyDeletePreviewQuery,
  useDeletePropertiesBulkMutation,
  useLazyExportPropertiesQuery,
  useUpdatePolicyBulkMutation,
  useGetPropertiesAccessibilityInfoQuery,
  useEditConfigSetMutation,
  useReassignConfigSetMutation,
  // useUpdateAccessToBillingMethodMutation, // @deprecated
  // useUpdateReadOnlyAccessMutation, // @deprecated
  useLazyExportPropertyManagersQuery,
  useGetTeamMembersCSVTemplateQuery,
  usePreviewTeamMembersCSVUploadMutation,
  useUploadTeamMembersCSVMutation,
  useResidentsSkipAutoScreeningEnableMutation,
  useResidentsSkipAutoScreeningDisableMutation,
  useGetAffordableHandlingQuery,
  useChangeAffordableHandlingMutation,
  useLazyGetAffordableHandlingPmsPreviewQuery,
  useGetAffordableHandlingPmsPreviewQuery,
  useGetFeeSuppressionQuery,
  useChangeFeeSuppressionMutation,
  usePreviewFeeSuppressionMutation,
  useGetUnitSuppressionStatusQuery,
} = propertyApi;
